import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import DonationButton from './DonationButton';
import { Link, useLocation } from 'react-router-dom';
import { IoGlasses } from "react-icons/io5";
import Footer from './Footer';

function RemovePages() {
  const [pdf, setPdf] = useState(null);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const location = useLocation();
  const isMainPage = location.pathname === '/';

  const generatePreviews = async (arrayBuffer) => {
    try {
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pageCount = pdfDoc.getPageCount();
      const previewPromises = Array.from({ length: pageCount }, async (_, i) => {
        const previewDoc = await PDFDocument.create();
        const [page] = await previewDoc.copyPages(pdfDoc, [i]);
        previewDoc.addPage(page);
        const pdfBytes = await previewDoc.save();
        return {
          pageNumber: i + 1,
          url: URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }))
        };
      });
      
      const newPreviews = await Promise.all(previewPromises);
      setPreviews(newPreviews);
    } catch (error) {
      console.error('Error generating previews:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      setPdf(arrayBuffer);
      setSelectedPages(new Set());
      await generatePreviews(arrayBuffer);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  };

  const togglePage = (pageIndex) => {
    setSelectedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageIndex)) {
        newSet.delete(pageIndex);
      } else {
        newSet.add(pageIndex);
      }
      return newSet;
    });
  };

  const removePages = async () => {
    if (!pdf || selectedPages.size === 0) return;
    
    setLoading(true);
    try {
      const pdfDoc = await PDFDocument.load(pdf);
      const newPdf = await PDFDocument.create();
      
      for (let i = 0; i < previews.length; i++) {
        if (!selectedPages.has(i)) {
          const [page] = await newPdf.copyPages(pdfDoc, [i]);
          newPdf.addPage(page);
        }
      }
      
      const modifiedPdf = await newPdf.save();
      const blob = new Blob([modifiedPdf], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'modified.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error removing pages:', error);
    }
    setLoading(false);
  };

  // Очистка URL при размонтировании компонента
  React.useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [previews]);

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <header className="text-center">
        <Link to="/pdf-tools" className="text-4xl font-bold text-black">
          <div className="flex items-center gap-2 justify-center">
            <IoGlasses size={50} />
            PDF llama
          </div>
        </Link>
        {!isMainPage && (
          <div className="mt-1">
            <Link to="/pdf-tools" className="text-gray-600 hover:text-gray-800">
              Go to another operations with PDF
            </Link>
          </div>
        )}
      </header>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mt-2">
        <h1 className="text-3xl font-bold mb-6">Remove PDF Pages</h1>
        
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileUpload}
          // className="mb-4"
          className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
        />

        {previews.length > 0 && (
          <div>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {previews.map((preview, i) => (
                <div key={i} className="relative bg-gray-50 rounded-lg overflow-hidden">
                  <embed
                    src={preview.url}
                    type="application/pdf"
                    className="w-full h-40"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-center">
                    Page {preview.pageNumber}
                  </div>
                  <div className="absolute top-2 right-2">
                    <input
                      type="checkbox"
                      checked={selectedPages.has(i)}
                      onChange={() => togglePage(i)}
                      className="h-5 w-5 bg-white"
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={removePages}
              disabled={loading || selectedPages.size === 0}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg mb-6 hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Processing...' : `Remove Selected Pages (${selectedPages.size})`}
            </button>
          </div>
        )}

      </div>
      <div className="mt-8">
        <DonationButton />
      </div>

      <article className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">
        Remove pages from PDF online
      </h1>
      
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Free PDF page deletion tool
        </h2>
        <p className="mb-4">
          Need to delete pages from your PDF document? Our free online tool lets you 
          quickly remove unwanted pages from any PDF file.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          How to remove pages from PDF online
        </h2>
        <div className="space-y-4">
          <h3 className="text-xl font-medium">Simple steps to edit your PDF:</h3>
          <div className="pl-4">
            <p className="mb-2">
              1. Upload your PDF file to our online editor
            </p>
            <p className="mb-2">
              2. Select the pages you want to remove from your document
            </p>
            <p className="mb-2">
              3. Click "Delete selected pages" to remove them
            </p>
            <p className="mb-2">
              4. Download your edited PDF file
            </p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          Features of our free PDF page removal Tool
        </h2>
        <div className="space-y-4">
          <h3 className="text-xl font-medium">Why choose out PDF llama editor:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Free online tool — no software installation needed
            </li>
            <li>
              Easily remove single or multiple pages from your PDF
            </li>
            <li>
              Secure PDF editing with automatic file deletion
            </li>
            <li>
              Preview pages before deleting them
            </li>
            <li>
              Compatible with all PDF versions
            </li>
            <li>
              No registration required to edit your PDFs
            </li>
          </ul>
        </div>
      </section>

    </article>

    <Footer />

    </div>
  );
}

export default RemovePages;
